import React from 'react';
import { WindowState } from '@/contexts/WindowManager';
import { cn } from '@/lib/utils';

interface WindowThumbnailProps {
  window: WindowState;
  className?: string;
  showTitle?: boolean;
  onClick?: () => void;
}

export const WindowThumbnail: React.FC<WindowThumbnailProps> = ({ 
  window, 
  className,
  showTitle = false,
  onClick 
}) => {
  const fallbackIcon = (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/30 to-muted/10 border border-border/30 rounded-lg">
      <div className="w-6 h-4 border border-muted-foreground/30 rounded-sm bg-muted/20" />
    </div>
  );

  if (!window.thumbnail) {
    return (
      <div 
        className={cn("relative overflow-hidden", className)}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {fallbackIcon}
        {showTitle && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1 py-0.5 truncate">
            {window.title}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "relative overflow-hidden bg-muted/10 border border-border/20 rounded-lg transition-all duration-200",
        onClick && "cursor-pointer hover:border-primary/30 hover:shadow-sm",
        className
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <img 
        src={window.thumbnail} 
        alt={`${window.title} thumbnail`}
        className="w-full h-full object-cover"
        style={{ 
          imageRendering: 'crisp-edges',
          // Ensure consistent aspect ratio
          aspectRatio: '4/3'
        }}
        onError={(e) => {
          // Fallback to generic icon if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = fallbackIcon.props.children;
            parent.className = cn(parent.className, "bg-gradient-to-br from-muted/30 to-muted/10");
          }
        }}
      />
      
      {/* Minimized overlay indicator */}
      <div className="absolute top-1 right-1 w-2 h-2 bg-warning/80 rounded-full" />
      
      {showTitle && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1 py-0.5 truncate">
          {window.title}
        </div>
      )}
    </div>
  );
};